import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { wallClock } from './DataTable';

/**
 * 시간 칸(#343 윤일향 「날짜 옆 시간 불편 — 구 버스인처럼 시간 조정」).
 *
 * 브라우저 `type="time"` 은 오전/오후 칸이 따로 있고 칸마다 끊겨 입력이 번거롭다. 24시간 `HH:mm` 글자 칸 하나로 받는다.
 * - 숫자만 쳐도 된다: `830`·`0830`·`8:30` → 08:30 · `14` → 14:00.
 * - ↑↓ = 커서가 시 쪽이면 1시간, 분 쪽이면 1분(Shift = 10분) — 레거시 시간 조정과 같은 손놀림.
 * - 비우면 '' (호출부가 「시간 없음」을 정한다). 못 읽는 값은 칸을 떠날 때 원래 값으로 돌린다.
 */
export function parseTime(raw: string): string | null {
  const s = raw.trim();
  if (s === '') return '';
  let h: number;
  let m: number;
  const colon = /^([0-9]{1,2}):([0-9]{1,2})$/.exec(s);
  if (colon) {
    h = Number(colon[1]);
    m = Number(colon[2]);
  } else if (/^[0-9]{1,4}$/.test(s)) {
    if (s.length <= 2) [h, m] = [Number(s), 0];
    else if (s.length === 3) [h, m] = [Number(s.slice(0, 1)), Number(s.slice(1))];
    else [h, m] = [Number(s.slice(0, 2)), Number(s.slice(2))];
  } else return null;
  if (h > 23 || m > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function TimeInput({ value, onChange, title, className }: {
  value: string;
  onChange: (v: string) => void;
  title?: string;
  className?: string;
}) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  // ↑↓ 뒤 시/분 선택을 렌더 직후에 되돌린다 — rAF 로 미루면 빠르게 두 번 누를 때 두 번째가 커서 끝(분)으로 간다(#343 실측).
  const inputRef = useRef<HTMLInputElement>(null);
  const sel = useRef<[number, number] | null>(null);
  const justFocused = useRef(false);
  useLayoutEffect(() => {
    if (sel.current && inputRef.current) inputRef.current.setSelectionRange(...sel.current);
    sel.current = null;
  });

  const commit = () => {
    const t = parseTime(text);
    if (t === null) return setText(value);
    setText(t);
    if (t !== value) onChange(t);
  };

  const step = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') return commit();
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const hourSide = (e.currentTarget.selectionStart ?? 0) <= 2;
    const base = parseTime(text) || parseTime(value) || '00:00';
    const [h = 0, m = 0] = base.split(':').map(Number);
    const delta = (e.key === 'ArrowUp' ? 1 : -1) * (hourSide ? 60 : e.shiftKey ? 10 : 1);
    const total = (((h * 60 + m + delta) % 1440) + 1440) % 1440;
    const next = `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
    sel.current = hourSide ? [0, 2] : [3, 5];
    setText(next);
    onChange(next);
  };

  return (
    <input
      ref={inputRef}
      className={`field w-[5.5rem] text-center tabular-nums ${className ?? ''}`}
      inputMode="numeric"
      placeholder="00:00"
      maxLength={5}
      title={title ?? '숫자만 쳐도 됩니다(0830 → 08:30) · ↑↓ 로 시·분 조정'}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        // 다 친 값은 바로 반영 — 칸을 떠나지 않고 저장을 눌러도 들어가게.
        if (/^[0-9]{4}$|^[0-9]{1,2}:[0-9]{2}$/.test(e.target.value.trim())) {
          const t = parseTime(e.target.value);
          if (t) onChange(t);
        }
      }}
      // 들어올 때 전체 선택 — 00:00 이 차 있어도 치는 대로 바뀐다(5글자 제한에 막히지 않게). 한 번 더 누르면 커서로 시/분을 고른다.
      onFocus={(e) => {
        e.currentTarget.select();
        justFocused.current = true;
      }}
      onMouseUp={(e) => {
        if (justFocused.current) e.preventDefault();
        justFocused.current = false;
      }}
      onBlur={commit}
      onKeyDown={step}
    />
  );
}

/**
 * 출발 일시 — 날짜 + 시간 두 칸(#343). 시간을 비우면 00:00(레거시 일반 노선의 「시각 미정」 관례 · 17320 등).
 */
export function DepartInput({ value, onChange }: { value: string | undefined; onChange: (v: string) => void }) {
  const [date, time] = wallSplit(value);
  const [shown, onDate, onTime] = usePendingTime(date, time, (d, t) => onChange(d === '' ? '' : `${d}T${t === '' ? '00:00' : t}`));
  return (
    <div className="flex gap-1">
      <DateField value={date} onChange={onDate} />
      <TimeInput value={shown} onChange={onTime} />
    </div>
  );
}

/** 'YYYY-MM-DDTHH:mm…'(또는 공백 구분) → [날짜, HH:mm]. 앞 10글자로 자르지 않는다 — 연도가 길면 시간 칸에 날짜 조각이 들어간다. */
function wallSplit(v: string | undefined): [string, string] {
  const [date = '', rest = ''] = (v ?? '').replace(' ', 'T').split('T');
  return [date, rest.slice(0, 5)];
}

/**
 * 날짜 칸 — `max` 를 줘야 Chrome 이 연도를 4자리에서 끊고 월로 넘어간다.
 * 없으면 20261025 를 치면 연도가 202610 이 되어 값이 깨진다(#343 실측).
 */
function DateField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input type="date" min="2000-01-01" max="9999-12-31" className="field min-w-0 flex-1" value={value}
           onChange={(e) => onChange(e.target.value)} />
  );
}

/**
 * 날짜가 아직 없을 때 친 시간은 값(날짜+시간 한 문자열)에 담을 수 없다 — 칸이 들고 있다가 날짜를 고르면 함께 보낸다.
 * (안 그러면 시간부터 치고 날짜를 고를 때 시간이 지워진다.)
 */
function usePendingTime(date: string, time: string, emit: (d: string, t: string) => void) {
  const [pending, setPending] = useState('');
  const shown = date === '' ? pending : time;
  const onDate = (d: string) => emit(d, shown);
  const onTime = (t: string) => (date === '' ? setPending(t) : emit(date, t));
  return [shown, onDate, onTime] as const;
}

/**
 * 도착 일시(#292) — 날짜는 꼭, 시간은 비워도 된다.
 *
 * 도착 시각은 접수 때 모르는 경우가 많다(1박 2일 등). `datetime-local` 한 칸은 날짜만 고를 수 없어
 * 아무 시각이나 들어갔다(입력한 시각과 같은 값). 시간을 비우면 **그날 23:59** 로 저장한다 —
 * 겹침 검사가 그날 끝까지를 막아 안전한 쪽이고, 화면은 23:59 를 「시간 없음」으로 읽어 날짜만 보인다.
 */
export const NO_TIME = '23:59';

/** 'YYYY-MM-DDTHH:mm'(또는 공백) → 날짜·시간. 23:59 는 시간 없음. */
function split(v: string | undefined): [string, string] {
  const [date, time] = wallSplit(v);
  return [date, time === NO_TIME ? '' : time];
}

export function ArrivalInput({ value, onChange }: { value: string | undefined; onChange: (v: string) => void }) {
  const [date, time] = split(value);
  const [shown, onDate, onTime] = usePendingTime(date, time, (d, t) => onChange(d === '' ? '' : `${d}T${t === '' ? NO_TIME : t}`));
  return (
    <div className="flex gap-1">
      <DateField value={date} onChange={onDate} />
      <TimeInput
        title="모르면 비워 두세요 — 날짜만 저장됩니다 · 0830 → 08:30 · ↑↓ 로 시·분 조정"
        value={shown}
        onChange={onTime}
      />
    </div>
  );
}

/**
 * 숙박 다일 운행(#325) — 레거시는 복귀일을 **왕복 복편 출발**에 넣었다(2025~ 일반 531건 전부).
 * 편도 도착에 복귀일을 넣고 왕복·숙박을 켜면 그 값을 복편 출발에도 넣는다.
 * 🔴 편도 도착은 그대로 둔다 — 출발일로 당기면 숙박 중간날이 배정 겹침 검사(편도 구간)에서 빠진다.
 * 도착이 출발과 같은 날이거나 복편 출발이 이미 있으면 손대지 않는다(당일 왕복·새벽 도착 편도 보호).
 */
export function moveReturnToShuttle(r: {
  startTime?: string;
  endTime?: string;
  endDeptTime?: string;
}): { isShuttle: true; endDeptTime: string } | null {
  const start = (r.startTime ?? '').slice(0, 10);
  const end = (r.endTime ?? '').slice(0, 10);
  if (start === '' || end === '' || end <= start || (r.endDeptTime ?? '') !== '') return null;
  return { isShuttle: true, endDeptTime: r.endTime!.replace(' ', 'T') };
}

/**
 * 출발 날짜를 따라가는 도착 날짜(#436 윤일향 — 출발→도착 · 복편 출발→복편 도착).
 * 도착이 비었거나 옛 출발과 같은 날(따라오던 값)이거나 새 출발보다 앞서면 새 출발 날짜로 옮긴다 — 시간은 그대로(비었으면 시간 없음).
 * 🔴 출발과 다른 날을 일부러 넣은 도착(숙박 다일)은 건드리지 않는다. 바꿀 게 없으면 null.
 */
export function followDate(prevFrom: string | undefined, nextFrom: string, to: string | undefined): string | null {
  const nd = wallSplit(nextFrom)[0];
  if (nd === '') return null;
  const pd = wallSplit(prevFrom)[0];
  const [td, tt] = wallSplit(to);
  if (td !== '' && td !== pd && td >= nd) return null;
  const next = `${nd}T${tt === '' ? NO_TIME : tt}`;
  return next === (to ?? '').replace(' ', 'T').slice(0, 16) ? null : next;
}

/** 도착 일시 표시 — 시간 없음(23:59)이면 날짜만. */
export const arrivalClock = (v: unknown) => {
  const s = wallClock(v);
  return s.endsWith(` ${NO_TIME}`) ? s.slice(0, 10) : s;
};
